import { useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { seasonAllFixturesPreset } from '@aelive/dds-client';
import { useDDSGetData } from '../../dds/useDDSGetData';
import { toRow, buildFixtureTeamsPath } from './adminFixturesRowMapper';

/* ------------------------------------------------------------------ */
/* COMPONENT */
/* ------------------------------------------------------------------ */

export default function AdminFixturesPage() {
  const navigate = useNavigate();
  const { competitionId, seasonId } = useParams();
  const location = useLocation();

  const seasonName =
    location.state?.seasonName || `Season ${seasonId}`;

  // OPUS-1403 — season.all_fixtures via the opt-in seasonAllFixturesPreset:
  // the whole season's fixtures for this competition/season, not clamped to
  // the ±1-week window season.fixture applies. limit:380 covers a full
  // league season (DDS caps at ALL_FIXTURES_MAX_LIMIT=500 regardless).
  const preset = useMemo(
    () =>
      seasonAllFixturesPreset({
        competition_id: competitionId,
        season_id: seasonId,
        limit: 380,
        includeVenue: true,
      }),
    [competitionId, seasonId],
  );

  const { data, loading, error } = useDDSGetData(preset, {
    enabled: Boolean(competitionId && seasonId),
  });

  const rows = useMemo(() => {
    const fixtures = data?.season?.[0]?.all_fixtures || [];
    return fixtures.map(toRow);
  }, [data]);

  /* ---------------- HANDLER ---------------- */

  const handleSelectFixture = (row) => {
    navigate(buildFixtureTeamsPath(competitionId, seasonId, row.id), {
      state: {
        fixtureName: row.fixtureName,
      },
    });
  };

  /* ---------------- COLUMNS ---------------- */

  const columns = [
    { field: 'fixtureName', headerName: 'FIXTURE NAME', flex: 1 },
    { field: 'status', headerName: 'STATUS', width: 140 },
    { field: 'date', headerName: 'DATE', width: 160 },
    { field: 'time', headerName: 'TIME', width: 120 },
    { field: 'venue', headerName: 'VENUE', flex: 1 },
    {
      field: 'actions',
      headerName: 'ACTIONS',
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="contained"
          sx={{
            backgroundColor: '#ffffff',
            color: '#000',
            textTransform: 'none',
          }}
          onClick={() => handleSelectFixture(params.row)}
        >
          Select
        </Button>
      ),
    },
  ];

  return (
    <Box
      sx={{
        border: '1px solid #ffffff',
        borderRadius: 1,
        p: 2,
        backgroundColor: '#1c1c1c',
      }}
    >
      {/* HEADER */}
      <Box sx={{ mb: 2 }}>
        {/* ROW 1: BACK */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ color: '#fff' }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>

        {/* ROW 2: TITLE */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              color: '#fff',
              fontSize: 18,
              fontWeight: 600,
            }}
          >
            {seasonName}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Typography sx={{ color: '#FF455C', fontSize: 14, mb: 1 }}>
          Couldn&apos;t load fixtures for this season. Please try again.
        </Typography>
      )}

      {/* GRID */}
      <Box sx={{ height: 420 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[10]}
          sx={{
            border: 'none',
            color: '#fff',
            backgroundColor: '#111',

            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#1a1a1a',
              borderBottom: '1px solid #333',
            },
            '& .MuiDataGrid-row': {
              borderBottom: '1px solid #333',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid #333',
            },
          }}
        />
      </Box>
    </Box>
  );
}
